import { useCallback, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { Edit, Check, AttachFile, FilePresent } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { AnswerDetail, Questions } from "../../Models/evaluation";
import colors from "../../Constants/colors";
import { useParams } from "react-router-dom";
import { useUploadDocument } from "../../Query/useCompanies";

export interface SectionFeedbackProps {
  sectionId: string;
  questions?: Questions[];
  answers: AnswerDetail[];
  onSaveFeedback: (
    sectionId: string,
    responses: Record<string, string>,
  ) => void;
}

export function SectionFeedback({
  sectionId,
  questions,
  answers,
  onSaveFeedback,
}: SectionFeedbackProps) {
  const { t } = useTranslation("singleCompanyComponent");
  const [isEditing, setIsEditing] = useState(false);
  const { companyId } = useParams<{ companyId: string }>();

  const uploadDocumentMutation = useUploadDocument(companyId!);

  const setInitialAnswers = useCallback(() => {
    const initialResponses: Record<string, string> = {};

    questions?.forEach((question) => {
      const answer = answers.find(
        (answer) => answer.question_key === question.question_key,
      )?.answer;

      if (answer) {
        initialResponses[question.question_key] = answer;
      }
    });

    return initialResponses;
  }, [questions, answers]);

  const [localResponses, setLocalResponses] =
    useState<Record<string, string>>(setInitialAnswers());

  const [attachedFiles, setAttachedFiles] = useState<Record<string, File[]>>(
    {},
  );

  const handleToggleEdit = () => {
    if (isEditing) {
      onSaveFeedback(sectionId, localResponses);
    }
    setIsEditing(!isEditing);
  };

  const handleUpdateLocalFeedback = (questionKey: string, value: string) => {
    if (!value) {
      setLocalResponses((prev) => {
        const updatedResponses = { ...prev };
        delete updatedResponses[questionKey];
        return updatedResponses;
      });
      return;
    }

    setLocalResponses((prev) => ({ ...prev, [questionKey]: value }));
  };

  const handleFileChange = async (
    questionKey: string,
    question_id: number,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!event.target.files) return;

    const filesArray = Array.from(event.target.files);

    setAttachedFiles((prev) => ({
      ...prev,
      [questionKey]: [...(prev[questionKey] || []), ...filesArray],
    }));
    for (const file of filesArray) {
      const formData = new FormData();
      formData.append("company_id", companyId!);
      formData.append("document_name", file.name);
      formData.append("file", file);
      formData.append("question_id", question_id.toString());

      uploadDocumentMutation.mutate(formData);
    }

    event.target.value = "";
  };

  return (
    <Box
      sx={{
        bgcolor: colors.orange50,
        borderRadius: 2,
        p: 2,
        border: `1px solid ${colors.orange200}`,
        borderLeft: `4px solid ${colors.orange600}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            color: colors.orange500,
            textTransform: "uppercase",
          }}
        >
          {t("sectionFeedback.title")}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleToggleEdit();
          }}
          sx={{
            color: colors.orange500,
            "&:hover": {
              bgcolor: colors.orange50,
            },
          }}
        >
          {isEditing ? <Check fontSize="medium" /> : <Edit fontSize="medium" />}
        </IconButton>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {questions &&
          questions.map((question, qIndex) => (
            <Box key={qIndex}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: colors.orange500,
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  fontSize: "0.95rem",
                }}
              >
                {question.question}
              </Typography>
              <TextField
                fullWidth
                size="small"
                disabled={!isEditing}
                value={localResponses[`${question.question_key}`] ?? ""}
                onChange={(e) =>
                  handleUpdateLocalFeedback(
                    question.question_key,
                    e.target.value,
                  )
                }
                placeholder={t("sectionFeedback.placeholder")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        component="label"
                        disabled={!isEditing}
                        sx={{ color: colors.orange500 }}
                      >
                        <AttachFile />
                        <input
                          hidden
                          type="file"
                          multiple
                          onChange={(e) =>
                            handleFileChange(
                              question.question_key,
                              question.question_id,
                              e,
                            )
                          }
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  bgcolor: "white",
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.875rem",
                    "& fieldset": {
                      borderColor: colors.orange500,
                      borderWidth: "1px",
                    },
                    "&:hover fieldset": {
                      borderColor: colors.orange500,
                      borderWidth: "1px",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.orange500,
                      borderWidth: "1px",
                    },
                    "&.Mui-disabled": {
                      "& fieldset": {
                        borderColor: colors.orange500,
                        color: colors.orange200,
                      },
                    },
                    "& .MuiOutlinedInput-input": {
                      color: colors.orange500,
                    },
                  },
                }}
              />
              {attachedFiles[question.question_key]?.length > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    bgcolor: colors.orange50,
                    borderRadius: 2,
                    p: 1.5,
                    border: `1px solid ${colors.orange200}`,
                    borderLeft: `4px solid ${colors.orange600}`,
                  }}
                >
                  <List dense disablePadding>
                    {attachedFiles[question.question_key].map((file, index) => (
                      <ListItem key={index} disablePadding sx={{ py: 0.2 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <FilePresent
                            sx={{
                              color: colors.orange600,
                              fontSize: "1.5rem",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          primaryTypographyProps={{
                            variant: "caption",
                            sx: {
                              fontFamily: "monospace",
                              fontSize: "0.8rem",
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          ))}
      </Box>
    </Box>
  );
}
